import { z } from 'zod';
import type { AdminPageMeta } from './admin.js';
import {
  CancellationPolicySchema,
  type CancellationPolicySource,
  type CancellationPolicyTier,
} from './cancellation-policy.js';
import type {
  EntityVerificationStatus,
  OwnerVerificationState,
  VerificationRequestRef,
} from './verification.js';

/**
 * F42 — Admin Venue Management frozen API contracts.
 *
 * The internal-staff venue-ops console: search / edit (with per-field admin
 * lock) / suspend / restore / merge / flag / bulk, plus the venue-scoped admin
 * audit timeline. Every mutation also writes an F06 `admin_audit_log` row (the
 * onSend hook) AND a `venue_admin_audit` row (before/after + reason +
 * correlation_id). Claim approval REUSES F11 verbatim (the D5 read/decide
 * permission split is in `admin.ts`). Cross-feature sub-tabs DEEP-LINK into
 * F38/F39/F40/F41 (+ reserved F44) — F42 never duplicates them.
 *
 * Wave-2 sub-agents develop against these contracts in parallel — DO NOT
 * rename/drop/add a field without escalating to the orchestrator first
 * (contract-amendment protocol).
 *
 * 🔴 `venues.tier` (F11 listing/claim tier `tier_1/2/3`) + `venues.status` enum
 * are OFF-LIMITS to F42. `verificationStatus` is a stored read-only Badge slot —
 * F09 owns its transition workflow + the blue-tick render.
 *
 * See plans/42_F42_admin_venue_management.md §2/§4/§5.
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/**
 * F09 v2 (E41) — the admin venue-list verification filter:
 * `verified | unverified | open_request` (an open initial request exists).
 */
export const ADMIN_VENUE_VERIFICATION_FILTERS = ['verified', 'unverified', 'open_request'] as const;
export type AdminVenueVerificationFilter = (typeof ADMIN_VENUE_VERIFICATION_FILTERS)[number];

/** `venue_admin_audit.action` vocabulary. */
export const VENUE_ADMIN_AUDIT_ACTIONS = [
  'venue.admin_edit',
  'venue.field_locked',
  'venue.field_unlocked',
  'venue.suspended',
  'venue.restored',
  'venue.flagged',
  'venue.flag_resolved',
  'venue.note',
  'venue.merged',
  'venue.merge_source',
  'venue.bulk_suspended',
  'venue.bulk_restored',
  'venue.bulk_flagged',
  // F09 v2 (E36) — native audit mirror for VENUE verification decisions,
  // written in the decision transaction. Same suffix set as
  // `user.verification_*` / `verification.*` (VERIFICATION_AUDIT_ACTION_SUFFIXES).
  'venue.verification_approved',
  'venue.verification_rejected',
  'venue.verification_more_info_requested',
  'venue.verification_revoked',
  'venue.verification_direct_verified',
  'venue.verification_name_change_applied',
  'venue.verification_cooldown_lifted',
  'venue.verification_rerequest_unlocked',
  'venue.verification_name_lock_override',
  'venue.verification_candidate_invited',
  'venue.verification_candidate_dismissed',
  'venue.verification_watchlist_resolved',
] as const;
export type VenueAdminAuditAction = (typeof VENUE_ADMIN_AUDIT_ACTIONS)[number];

/** Bulk action set (D7 — force-resync dropped). */
export const BULK_VENUE_ACTIONS = ['suspend', 'restore', 'flag'] as const;
export type BulkVenueAction = (typeof BULK_VENUE_ACTIONS)[number];

/** Merge impact-preview conflict kinds (1:1-unique rows that can't move — §2.6). */
export const MERGE_CONFLICT_KINDS = [
  'BILLING_CONFLICT',
  'SUBSCRIPTION_CONFLICT',
  'PAYMENT_CONFIG_CONFLICT',
] as const;
export type MergeConflictKind = (typeof MERGE_CONFLICT_KINDS)[number];

/** F42 error codes (HTTP-layer). `STALE_VENUE` is reused from F12. */
export const ADMIN_VENUE_ERROR_CODES = [
  'FIELD_ADMIN_LOCKED',
  'STALE_VENUE',
  'ALREADY_MERGED',
  'MERGE_SAME_VENUE',
  'MERGE_CANONICAL_INVALID',
  'BULK_CAP_EXCEEDED',
  'REASON_REQUIRED',
  'VENUE_NOT_SUSPENDABLE',
] as const;
export type AdminVenueErrorCode = (typeof ADMIN_VENUE_ERROR_CODES)[number];

// ─── Constants ───────────────────────────────────────────────────────────────

export const BULK_VENUE_CAP = 100;
export const SUSPEND_REASON_MIN = 10;
export const MERGE_REASON_MIN = 10;
export const FLAG_REASON_MIN = 5;
export const VENUE_NOTE_MAX = 2000;
export const REASON_MAX = 2000;

// ─── Zod input schemas ───────────────────────────────────────────────────────

/** Admin edit of one or more venue fields (If-Match required at the route). */
export const AdminEditVenueFieldsInputSchema = z
  .object({
    fields: z.record(z.string(), z.unknown()),
    reason: z.string().max(REASON_MAX).optional(),
    /** Optionally lock the edited fields against future owner edits. */
    lockFields: z.array(z.string()).optional(),
  })
  .strict();
export type AdminEditVenueFieldsInput = z.infer<typeof AdminEditVenueFieldsInputSchema>;

export const LockFieldsInputSchema = z
  .object({
    fields: z.array(z.string()).min(1),
    reason: z.string().max(REASON_MAX).optional(),
  })
  .strict();
export type LockFieldsInput = z.infer<typeof LockFieldsInputSchema>;

export const UnlockFieldsInputSchema = z
  .object({
    fields: z.array(z.string()).min(1),
    reason: z.string().max(REASON_MAX).optional(),
  })
  .strict();
export type UnlockFieldsInput = z.infer<typeof UnlockFieldsInputSchema>;

/** Suspend — mandatory reason (destructive). */
export const SuspendVenueInputSchema = z
  .object({ reason: z.string().min(SUSPEND_REASON_MIN).max(REASON_MAX) })
  .strict();
export type SuspendVenueInput = z.infer<typeof SuspendVenueInputSchema>;

/** Restore — reason optional (constructive). */
export const RestoreVenueInputSchema = z
  .object({ reason: z.string().max(REASON_MAX).optional() })
  .strict();
export type RestoreVenueInput = z.infer<typeof RestoreVenueInputSchema>;

/** Flag-for-review — mandatory reason. */
export const FlagVenueInputSchema = z
  .object({ reason: z.string().min(FLAG_REASON_MIN).max(REASON_MAX) })
  .strict();
export type FlagVenueInput = z.infer<typeof FlagVenueInputSchema>;

export const ResolveFlagInputSchema = z
  .object({ note: z.string().max(VENUE_NOTE_MAX).optional() })
  .strict();
export type ResolveFlagInput = z.infer<typeof ResolveFlagInputSchema>;

export const AddVenueNoteInputSchema = z
  .object({ note: z.string().min(1).max(VENUE_NOTE_MAX) })
  .strict();
export type AddVenueNoteInput = z.infer<typeof AddVenueNoteInputSchema>;

/** Merge — mandatory reason; super_admin only (gated at the route). */
export const MergeVenuesInputSchema = z
  .object({
    sourceVenueId: z.string().uuid(),
    canonicalVenueId: z.string().uuid(),
    reason: z.string().min(MERGE_REASON_MIN).max(REASON_MAX),
  })
  .strict()
  .refine((v) => v.sourceVenueId !== v.canonicalVenueId, {
    message: 'MERGE_SAME_VENUE',
    path: ['sourceVenueId'],
  });
export type MergeVenuesInput = z.infer<typeof MergeVenuesInputSchema>;

export const MergePreviewQuerySchema = z
  .object({ sourceVenueId: z.string().uuid(), canonicalVenueId: z.string().uuid() })
  .strict();
export type MergePreviewQuery = z.infer<typeof MergePreviewQuerySchema>;

/** Bulk action — cap 100; mandatory reason. Idempotency-Key header at route. */
export const BulkVenueActionInputSchema = z
  .object({
    action: z.enum(BULK_VENUE_ACTIONS),
    venueIds: z.array(z.string().uuid()).min(1).max(BULK_VENUE_CAP),
    reason: z.string().min(FLAG_REASON_MIN).max(REASON_MAX),
  })
  .strict();
export type BulkVenueActionInput = z.infer<typeof BulkVenueActionInputSchema>;

/**
 * Admin per-venue cancellation policy override (If-Match required at the route).
 * `policy: null` resets the venue to the platform-default policy. Gated on
 * `bookings:edit_cancellation_policy` — the same permission F17 uses for the
 * platform default.
 */
export const AdminVenueCancellationPolicyInputSchema = z
  .object({
    policy: CancellationPolicySchema.nullable(),
    reason: z.string().max(REASON_MAX).optional(),
  })
  .strict();
export type AdminVenueCancellationPolicyInput = z.infer<
  typeof AdminVenueCancellationPolicyInputSchema
>;

// ─── Read shapes ─────────────────────────────────────────────────────────────

/** One admin-locked field surfaced to the F12 owner editor. */
export interface VenueFieldLock {
  readonly fieldPath: string;
  readonly reason: string | null;
  readonly lockedAt: string;
  readonly lockedByDisplay: string | null;
}

/** One venue_admin_audit timeline row (admin-facing). */
export interface VenueAdminAuditEntry {
  readonly id: string;
  readonly action: VenueAdminAuditAction | string;
  readonly fieldPath: string | null;
  readonly beforeSnapshot: unknown;
  readonly afterSnapshot: unknown;
  readonly reason: string | null;
  readonly correlationId: string | null;
  readonly actorAdminId: string | null;
  readonly actorDisplay: string | null;
  readonly createdAt: string;
}

/** Row in the admin venue table. */
export interface AdminVenueListRow {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly status: string;
  readonly tier: string;
  readonly claimStatus: string;
  /** F09 v2 (E3) — the 2-valued venue tick column; render from `verificationState`. */
  readonly verificationStatus: EntityVerificationStatus;
  /** F09 v2 (E2/E41) — the derived owner-state of the venue's tick. */
  readonly verificationState: OwnerVerificationState;
  /** F09 v2 — the open `initial` request, if any (null when none). */
  readonly openVerificationRequest: VerificationRequestRef | null;
  readonly ownerB2bAccountId: string | null;
  readonly cityCode: string;
  readonly governorateIso: string;
  readonly flaggedForReviewAt: string | null;
  readonly suspendedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Friendly location names (joined from F04 geo) — fall back to codes if null. */
  readonly cityName: string | null;
  readonly governorateName: string | null;
  /** Provenance + first Google photo ref (for the row avatar) + sport tags. */
  readonly source: string | null;
  readonly firstGooglePhoto: string | null;
  readonly sports: ReadonlyArray<{ readonly sportName: string; readonly isPrimary: boolean }>;
}

/**
 * KPI summary + page-numbered pagination returned in the list `meta`.
 *
 * The `total`/`active`/`suspended`/`claimed`/`flagged` numbers are GLOBAL
 * (unfiltered) KPI counts for the dashboard Stat strip — NOT the filtered
 * result count. The page-numbered pagination is carried in `pagination`:
 * `pagination.total` is the FILTERED row count (a COUNT(*) over the same WHERE
 * clause). `nextCursor` is retained for back-compat with any cursor consumer.
 */
export interface AdminVenueListMeta {
  readonly total: number;
  readonly active: number;
  readonly suspended: number;
  readonly claimed: number;
  readonly flagged: number;
  readonly nextCursor: string | null;
  /** Page-numbered pagination over the FILTERED result set. */
  readonly pagination: AdminPageMeta;
}

/** Which cross-feature deep-links are available for this venue (§2.5). */
export interface AdminVenueDeepLinks {
  readonly subscription: boolean;
  readonly placements: boolean;
  readonly promotions: boolean;
  readonly tournaments: boolean;
  readonly financials: boolean; // reserved (F44); false until it ships
  readonly bookings: boolean;
  readonly staffAccountId: string | null;
  /** F09 v2 (E41) — the `/verifications/entities/venue/:id` deep link is available. */
  readonly verification: boolean;
}

/** Full admin venue detail (the detail-view payload). */
export interface AdminVenueDetail {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly status: string;
  readonly tier: string;
  readonly claimStatus: string;
  /** F09 v2 (E3) — the 2-valued venue tick column; render from `verificationState`. */
  readonly verificationStatus: EntityVerificationStatus;
  /** F09 v2 — derived owner-state of the venue's tick. */
  readonly verificationState: OwnerVerificationState;
  /** F09 v2 — the open `initial` request, if any. */
  readonly openVerificationRequest: VerificationRequestRef | null;
  readonly ownerB2bAccountId: string | null;
  readonly mergedIntoVenueId: string | null;
  readonly suspension: {
    readonly suspendedAt: string | null;
    readonly suspendedByAdminId: string | null;
    readonly suspensionReason: string | null;
  };
  readonly flag: {
    readonly flaggedForReviewAt: string | null;
    readonly flaggedForReviewByAdminId: string | null;
    readonly flagReason: string | null;
  };
  readonly adminLockedFields: readonly VenueFieldLock[];
  readonly recentAudit: readonly VenueAdminAuditEntry[];
  readonly claimSummary: {
    readonly pendingCount: number;
    readonly latestStatus: string | null;
  };
  /**
   * The venue's RESOLVED cancellation policy: the venue's own tiers when it has
   * an override (`source: 'venue_override'`), else the platform-default tiers
   * (`source: 'platform_default'`). Admins edit this via
   * `PATCH /admin/venues/:id/cancellation-policy`.
   */
  readonly cancellationPolicy: {
    readonly tiers: readonly CancellationPolicyTier[];
    readonly source: CancellationPolicySource;
  };
  readonly deepLinks: AdminVenueDeepLinks;
  readonly updatedAt: string;
}

/**
 * Rich venue profile for the admin detail page. Unlike `GET /public/venues`
 * (which is active-only), this is served for ANY status — including drafts —
 * so admins can review the full record before publishing.
 */
export interface AdminVenueProfile {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly status: string;
  readonly tier: string;
  readonly claimStatus: string;
  /** F09 v2 (E3) — the 2-valued venue tick column (`unverified | verified`). */
  readonly verificationStatus: EntityVerificationStatus;
  /** Provenance: 'google' | 'osm' | 'google+osm' for scraped rows; null otherwise. */
  readonly source: string | null;
  readonly googlePlaceId: string | null;
  /** Google Places photo names (refs) — the admin UI builds media URLs from these. */
  readonly googlePhotos: readonly string[];
  readonly description: string | null;
  readonly phone: string | null;
  readonly whatsappPhone: string | null;
  readonly streetAddress: string | null;
  readonly lat: string | null;
  readonly lng: string | null;
  readonly governorate: { readonly iso: string | null; readonly name: string | null };
  readonly caza: { readonly code: string | null; readonly name: string | null };
  readonly city: { readonly code: string | null; readonly name: string | null };
  readonly timezone: string | null;
  readonly countryCode: string | null;
  readonly createdByKind: string | null;
  readonly bookable: boolean;
  readonly taxMode: string | null;
  readonly taxRatePercent: string | null;
  readonly openingHours: unknown;
  readonly socialLinks: Readonly<Record<string, string>> | null;
  readonly sports: ReadonlyArray<{ readonly sportName: string; readonly isPrimary: boolean }>;
  readonly courts: ReadonlyArray<{
    readonly id: string;
    readonly name: string;
    readonly ordinal: number;
    readonly sportName: string | null;
    readonly capacity: number | null;
    readonly status: string;
  }>;
  readonly photos: ReadonlyArray<{
    readonly id: string;
    readonly cdnUrl: string | null;
    readonly moderationStatus: string;
  }>;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Pre-merge impact preview. `reparent` = per-table row counts that will move. */
export interface MergeConflict {
  readonly kind: MergeConflictKind;
  readonly table: string;
  readonly detail: string;
}
export interface MergeImpactPreview {
  readonly sourceVenueId: string;
  readonly canonicalVenueId: string;
  readonly reparent: Readonly<Record<string, number>>;
  readonly conflicts: readonly MergeConflict[];
}

/** Per-item result of a bulk action (partial success). */
export interface BulkActionResultItem {
  readonly venueId: string;
  readonly ok: boolean;
  readonly error?: string;
}
export interface BulkActionResult {
  readonly correlationId: string;
  readonly results: readonly BulkActionResultItem[];
}

/** Hosted-tournament row (F41 deep-link via tournament_venue_hosts). */
export interface HostedTournamentRow {
  readonly tournamentId: string;
  readonly title: string;
  readonly status: string;
  readonly deepLink: string;
}
